import type { CSSProperties } from "react";
import { FP_SHOW_WHEN_ATTRIBUTE, type PublicQuote } from "@forgepoint/shared";
import { containerStyle, readContainerValues } from "./fpContainer";
import { resolveLineField } from "./lineFields";
import type { LineScopeValue } from "./lineScopeContext";
import { shouldShowWhen } from "./showWhen";

/** How a table stacks the values of a multi-break item line. */
export type FpBreakMode = "stack" | "rows";

/** Which interior rules a table draws between its cells. */
export type FpTableRules = "horizontal" | "vertical" | "grid" | "none";

/** The `rules` values a table may declare. */
const TABLE_RULES = ["horizontal", "vertical", "grid", "none"] as const;

/** The default padding of a table cell, which a column's `padding` overrides. */
export const TABLE_CELL_PADDING = "4px 6px";

/** The attribute carrying a source element's positional editor node id. */
const NODE_ID_ATTRIBUTE = "data-fp-node";

/**
 * The editor node id stamped on a source grid element, in the form a React
 * `data-fp-node` prop takes.
 * @param element - The source grid element
 * @returns The node id, or undefined when the element is not stamped
 */
export function fpNodeId(element: Element): string | undefined {
  return element.getAttribute(NODE_ID_ATTRIBUTE) ?? undefined;
}

/** One `<fp-column>` declaration inside an `<fp-table>`. */
export interface FpTableColumn {
  /** The declaring element's editor node id, so its cells select it. */
  nodeId: string | undefined;
  source: string | null;
  header: string | null;
  label: string | null;
  prefix: string | null;
  suffix: string | null;
  width: string | null;
  align: CSSProperties["textAlign"];
  /** True when the column's value varies per quantity break. */
  repeatBreaks: boolean;
  /** 1 for the main row; a higher number puts the cell on that secondary row. */
  row: number;
  /** How many columns the cell spans: "all", a count, or null for one. */
  span: "all" | number | null;
  /** How many rows a static cell spans downwards; 1 for an unmerged cell. */
  rowspan: number;
  showWhen: string | null;
  /** True once a gate has resolved false; only the editor still draws it. */
  hidden: boolean;
  style: CSSProperties;
  /** The `header-*` overrides, applied to the header cell only. */
  headerStyle: CSSProperties;
}

/** Text alignments a column may declare. */
const ALIGNMENTS = ["left", "center", "right"] as const;

/**
 * Parses a column's `align`, ignoring anything outside the vocabulary.
 * @param value - The raw `align` attribute value, or null when absent
 * @returns The alignment, or undefined
 */
function parseAlign(value: string | null): FpTableColumn["align"] {
  return ALIGNMENTS.find((candidate) => candidate === value);
}

/**
 * Parses one `<fp-column>` element into its declaration.
 * @param element - The `<fp-column>` element
 * @returns The parsed column declaration
 */
export function parseTableColumn(element: Element): FpTableColumn {
  return {
    nodeId: fpNodeId(element),
    source: element.getAttribute("source"),
    header: element.getAttribute("header"),
    label: element.getAttribute("label"),
    prefix: element.getAttribute("prefix"),
    suffix: element.getAttribute("suffix"),
    width: element.getAttribute("width"),
    align: parseAlign(element.getAttribute("align")),
    repeatBreaks: element.getAttribute("repeat") === "breaks",
    row: parseRow(element.getAttribute("row")),
    span: parseSpan(element.getAttribute("span")),
    rowspan: parseRowspan(element.getAttribute("rowspan")),
    showWhen: element.getAttribute(FP_SHOW_WHEN_ATTRIBUTE),
    hidden: false,
    style: containerStyle(readContainerValues(element)),
    headerStyle: parseHeaderStyle(element),
  };
}

/** The `fp-column` attribute painting the header cell's band. */
const HEADER_BACKGROUND_ATTRIBUTE = "header-background";

/** The `fp-column` attribute colouring the header cell's text. */
const HEADER_COLOR_ATTRIBUTE = "header-color";

/** The `fp-column` attribute sizing the header cell's text. */
const HEADER_SIZE_ATTRIBUTE = "header-size";

/** The `fp-column` attribute decorating the header cell's text. */
const HEADER_DECORATION_ATTRIBUTE = "header-decoration";

/**
 * Reads a column's `header-*` overrides, which style the header cell alone so a
 * header band can differ from the body cells below it.
 * @param element - The `<fp-column>` element
 * @returns The header-only style, empty when the column declares no override
 */
function parseHeaderStyle(element: Element): CSSProperties {
  return {
    ...headerProperty(
      "backgroundColor",
      element.getAttribute(HEADER_BACKGROUND_ATTRIBUTE),
    ),
    ...headerProperty("color", element.getAttribute(HEADER_COLOR_ATTRIBUTE)),
    ...headerProperty("fontSize", element.getAttribute(HEADER_SIZE_ATTRIBUTE)),
    ...headerProperty(
      "textDecoration",
      element.getAttribute(HEADER_DECORATION_ATTRIBUTE),
    ),
  };
}

/**
 * A one-property header style fragment, or nothing when the attribute is absent.
 * @param property - The CSS property the attribute sets
 * @param value - The raw attribute value, or null when absent
 * @returns The style fragment
 */
function headerProperty(
  property: keyof CSSProperties,
  value: string | null,
): CSSProperties {
  if (value === null || value.trim() === "") {
    return {};
  }
  return { [property]: value.trim() };
}

/** Whether a header cell starts from its column's body style or from nothing. */
export type FpHeaderMode = "inherit" | "own";

/**
 * Reads a table's `header-style`: `own` gives the header band a clean slate, so
 * the per-column body size, colour and padding stay out of it.
 * @param value - The raw `header-style` attribute value, or null when absent
 * @returns The header mode
 */
export function parseHeaderMode(value: string | null): FpHeaderMode {
  return value === "own" ? "own" : "inherit";
}

/**
 * Whether a column declares a header band of its own, which stands in for the
 * hairline a plain header draws under itself.
 * @param column - The column declaration
 * @returns True when the column paints its header cell
 */
export function hasHeaderBand(column: FpTableColumn): boolean {
  return column.headerStyle.backgroundColor !== undefined;
}

/**
 * Parses a column's `row`, defaulting to the main row.
 * @param value - The raw `row` attribute value, or null when absent
 * @returns The 1-based row the cell renders on
 */
function parseRow(value: string | null): number {
  const row = Number(value);
  return Number.isInteger(row) && row > 1 ? row : 1;
}

/**
 * Parses a column's `span`: the whole table width, a column count, or one.
 * @param value - The raw `span` attribute value, or null when absent
 * @returns The span, or null for a single column
 */
function parseSpan(value: string | null): "all" | number | null {
  if (value === "all") {
    return "all";
  }
  const span = Number(value);
  return Number.isInteger(span) && span > 1 ? span : null;
}

/**
 * Parses a static cell's `rowspan`: a row count, or one.
 * @param value - The raw `rowspan` attribute value, or null when absent
 * @returns The rowspan, at least one
 */
function parseRowspan(value: string | null): number {
  const rowspan = Number(value);
  return Number.isInteger(rowspan) && rowspan > 1 ? rowspan : 1;
}

/** The character splitting a column `header` into stacked lines. */
const HEADER_LINE_SEPARATOR = "|";

/**
 * Splits a column header into the lines it stacks, so `header="Price|$"` titles
 * the column with "Price" over "$".
 * @param header - The raw `header` attribute value, or null when absent
 * @returns One entry per header line, empty when the column declares no header
 */
export function headerLines(header: string | null): string[] {
  if (header === null) {
    return [];
  }
  return header.split(HEADER_LINE_SEPARATOR).map((line) => line.trim());
}

/** How a table draws the rules between its cells. */
export interface FpTableRuleStyle {
  model: FpTableRules;
  color: string;
}

/** The colour interior rules draw in when the table declares no `rule-color`. */
const DEFAULT_RULE_COLOR = "#d1d5db";

/**
 * Reads a table's interior rule model and colour, so the rules between cells can
 * differ from the colour of the table's own box border.
 * @param rules - The raw `rules` attribute value, or null when absent
 * @param ruleColor - The raw `rule-color` attribute value, or null when absent
 * @returns The interior rule style
 */
export function parseRuleStyle(
  rules: string | null,
  ruleColor: string | null,
): FpTableRuleStyle {
  return {
    model: parseTableRules(rules),
    color: parseRuleColor(ruleColor),
  };
}

/**
 * Reads a table's `rules`, defaulting to none. A priced table reads as a table
 * from its columns, not from a rule under every row: a table that wants interior
 * rules declares them.
 * @param value - The raw `rules` attribute value, or null when absent
 * @returns The interior rule model
 */
function parseTableRules(value: string | null): FpTableRules {
  return TABLE_RULES.find((candidate) => candidate === value) ?? "none";
}

/**
 * Reads a table's `rule-color`, falling back to the default rule colour.
 * @param value - The raw `rule-color` attribute value, or null when absent
 * @returns The CSS colour interior rules draw in
 */
function parseRuleColor(value: string | null): string {
  if (value === null || value.trim() === "") {
    return DEFAULT_RULE_COLOR;
  }
  return value.trim();
}

/**
 * Reads a table's `stripe`, the colour it bands alternate lines in.
 * @param value - The raw `stripe` attribute value, or null when absent
 * @returns The CSS colour to band with, or null when the table declares none
 */
export function parseStripeColor(value: string | null): string | null {
  if (value === null || value.trim() === "") {
    return null;
  }
  return value.trim();
}

/**
 * Whether a rule model draws a rule under each row.
 * @param rules - The table's interior rule model
 * @returns True when row rules are drawn
 */
export function hasRowRules(rules: FpTableRules): boolean {
  return rules === "horizontal" || rules === "grid";
}

/**
 * Whether a rule model draws a rule between columns.
 * @param rules - The table's interior rule model
 * @returns True when column rules are drawn
 */
export function hasColumnRules(rules: FpTableRules): boolean {
  return rules === "vertical" || rules === "grid";
}

/**
 * Resolves each column's `data-fp-show-when` gate structurally: does the table
 * need this column at all. A column is one cell per line and every line shares
 * one grid, so this answer must be the same for all of them - dropping a column
 * on one line alone would leave a hole in that row. A failing column leaves the
 * grid entirely in the customer render, so the remaining columns close up, and
 * stays drawn but marked in the editor, where a conditional column must remain
 * selectable. Whether one line's cell fills that column in is decided per line
 * by `cellRendersContent`.
 * @param columns - The parsed column declarations, in document order
 * @param isVisible - Whether a gate condition holds anywhere in the table
 * @param editable - True in the editor, where a failing column stays drawn
 * @returns The columns the table renders
 */
export function applyColumnVisibility(
  columns: FpTableColumn[],
  isVisible: (condition: string) => boolean,
  editable: boolean,
): FpTableColumn[] {
  const resolved = columns.map((column) =>
    column.showWhen === null || isVisible(column.showWhen)
      ? column
      : { ...column, hidden: true },
  );
  return editable ? resolved : resolved.filter((column) => !column.hidden);
}

/** A table grid element paired with its resolved `data-fp-show-when` gate. */
export interface FpGatedElement {
  element: Element;
  hidden: boolean;
}

/**
 * Resolves the `data-fp-show-when` gate on a run of `<fp-row>` or `<fp-cell>`
 * elements, matching how `applyColumnVisibility` treats a column.
 * @param elements - The row or cell elements, in document order
 * @param isVisible - Whether a gate condition holds
 * @param editable - True in the editor, where a failing element stays drawn
 * @returns The elements to render, each carrying its resolved gate
 */
export function gateElements(
  elements: Element[],
  isVisible: (condition: string) => boolean,
  editable: boolean,
): FpGatedElement[] {
  const resolved = elements.map((element) => {
    const condition = element.getAttribute(FP_SHOW_WHEN_ATTRIBUTE);
    return { element, hidden: condition !== null && !isVisible(condition) };
  });
  return editable ? resolved : resolved.filter((entry) => !entry.hidden);
}

/**
 * Reads a table's `break-mode`, defaulting to stacking values inside the cell.
 * @param value - The raw `break-mode` attribute value, or null when absent
 * @returns The break mode
 */
export function parseBreakMode(value: string | null): FpBreakMode {
  return value === "rows" ? "rows" : "stack";
}

/**
 * The direct children of an element with a given tag name.
 * @param element - The parent element
 * @param tagName - The lowercased tag name to match
 * @returns The matching direct children, in document order
 */
export function directChildren(element: Element, tagName: string): Element[] {
  return Array.from(element.children).filter(
    (child) => child.tagName.toLowerCase() === tagName,
  );
}

/**
 * Whether one line fills a column in: the column's own `data-fp-show-when` gate
 * holds against that line's scope, not merely somewhere in the table. A column
 * kept structurally by one line that carries a weight must still leave the cell
 * empty on every line that does not.
 *
 * A column the editor is only previewing (its gate failed for every line) always
 * renders, so an author can still see and select what the column binds.
 * @param quote - The public quote backing the document
 * @param scope - The line scope the cell resolves against
 * @param column - The column declaration
 * @returns True when the cell renders its content for this line
 */
export function cellRendersContent(
  quote: PublicQuote,
  scope: LineScopeValue,
  column: FpTableColumn,
): boolean {
  if (column.hidden || column.showWhen === null) {
    return true;
  }
  return shouldShowWhen(quote, scope, column.showWhen);
}

/**
 * Whether a column puts anything in one line's cell: its binding resolved, or it
 * binds nothing at all and is a pure label column. A column's literal `label`,
 * `prefix` and `suffix` decorate a bound value, so an unfilled binding takes
 * them with it - a cell holding no weight must not still print "Weight:".
 * @param quote - The public quote backing the document
 * @param scope - The line scope the cell resolves against
 * @param column - The column declaration
 * @returns True when the column fills this line's cell
 */
export function cellIsFilled(
  quote: PublicQuote,
  scope: LineScopeValue,
  column: FpTableColumn,
): boolean {
  if (column.source === null) {
    return true;
  }
  return cellHasValue(quote, scope, column);
}

/**
 * Whether a secondary row is worth drawing for one line: at least one of its
 * columns puts something in it. Without this an item carrying no description or
 * weight still occupies a blank sub-row under itself. The editor always draws
 * the row, so a sub-row the sample quote happens not to fill stays authorable.
 * @param quote - The public quote backing the document
 * @param scope - The line scope the row resolves against
 * @param columns - The columns sitting on the secondary row
 * @param editable - True in the editor, where an empty row stays drawn
 * @returns True when the row renders
 */
export function secondaryRowRenders(
  quote: PublicQuote,
  scope: LineScopeValue,
  columns: FpTableColumn[],
  editable: boolean,
): boolean {
  if (editable) {
    return true;
  }
  return columns.some(
    (column) =>
      cellRendersContent(quote, scope, column) &&
      cellIsFilled(quote, scope, column),
  );
}

/**
 * Whether a column's bound source resolves to something for one line.
 * @param quote - The public quote backing the document
 * @param scope - The line scope the cell resolves against
 * @param column - The column declaration
 * @returns True when the binding resolved a non-empty value
 */
function cellHasValue(
  quote: PublicQuote,
  scope: LineScopeValue,
  column: FpTableColumn,
): boolean {
  const resolved = resolveLineField(quote, scope, column.source);
  return resolved !== null && resolved.value !== "";
}

/**
 * The properties of a container style that must ride onto rendered prose rather
 * than stay on its cell. `fp-text` sets its own colour and font on the block it
 * draws, so a colour left on the enclosing cell never reaches the words.
 * @param style - The container style declared on the slot or cell
 * @returns The subset of properties the prose has to carry itself
 */
export function proseStyle(style: CSSProperties): CSSProperties {
  const { color, fontSize, fontFamily } = style;
  return {
    ...(color === undefined ? {} : { color }),
    fontSize: fontSize ?? "inherit",
    ...(fontFamily === undefined ? {} : { fontFamily }),
  };
}
